import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "../../components/DataTable";
import { Plus, FolderTree } from "lucide-react";
import { useGroups, useCreateGroup, type DeviceGroup } from "../../api/groups";
import { ApiError } from "../../api/client";
import { useMyCompany } from "../../api/companies";

export function GroupListPage() {
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupType, setGroupType] = useState("static");
  const [groupDescription, setGroupDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useGroups();
  const companyQuery = useMyCompany();
  const createMutation = useCreateGroup();
  const company = companyQuery.data?.data;

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await createMutation.mutateAsync({
        name: groupName,
        type: groupType,
        description: groupDescription || undefined,
      });
      setShowCreate(false);
      setGroupName("");
      setGroupType("static");
      setGroupDescription("");
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: { message?: string } } | undefined;
        setError(body?.error?.message ?? "Failed to create group");
      } else {
        setError("Failed to create group");
      }
    }
  }

  const columns = [
    { key: "name", header: "Group Name", sortable: true },
    { key: "type", header: "Type", sortable: true, render: (row: DeviceGroup) => <span className="capitalize">{row.type}</span> },
    { key: "deviceCount", header: "Devices", sortable: true },
    {
      key: "description",
      header: "Description",
      render: (row: DeviceGroup) => row.description
        ? <span className="text-gray-600 dark:text-riot-navy-300">{row.description}</span>
        : <span className="text-gray-400 dark:text-riot-navy-400">-</span>,
    },
    { key: "createdAt", header: "Created", sortable: true, render: (row: DeviceGroup) => new Date(row.createdAt).toLocaleDateString() },
  ];

  // deviceCount comes from the server, which counts each group's members
  // on its index. Recounting here from a page of devices undercounts every
  // group once the fleet outgrows that page.
  const groups = data?.data ?? [];

  return (
    <div>
      <div className="mb-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-riot-navy-700 dark:bg-riot-navy-800">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-riot-gold-600 dark:text-riot-gold-400">
              Device groups
            </p>
            <h2 className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
              Organize devices by customer, region, fleet, or function
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-riot-navy-300">
              {company
                ? `You are managing groups for ${company.companyName}.`
                : "Create groups to structure devices the way each company actually operates."}
            </p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center justify-center gap-1.5 rounded-md bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600"
          >
            <Plus size={14} /> Add group
          </button>
        </div>
      </div>

      {showCreate && (
        <form onSubmit={handleCreate} className="mb-4 rounded-lg border border-gray-200 bg-white p-5 dark:border-riot-navy-700 dark:bg-riot-navy-800">
          <h3 className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">Create Device Group</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">Group Name</label>
              <input
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="e.g. Fleet Alpha"
                required
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">Group Type</label>
              <select
                value={groupType}
                onChange={(e) => setGroupType(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              >
                <option value="static">Static — manually assigned</option>
                <option value="dynamic">Dynamic — rule-based (Standard+)</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">Description</label>
              <input
                type="text"
                value={groupDescription}
                onChange={(e) => setGroupDescription(e.target.value)}
                placeholder="Optional — what devices belong in this group?"
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              />
            </div>
          </div>
          {error && (
            <p className="mt-3 text-xs text-red-600 dark:text-red-400">{error}</p>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setShowCreate(false); setError(null); }}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || !groupName}
              className="rounded-md bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:opacity-50"
            >
              {createMutation.isPending ? "Creating..." : "Create Group"}
            </button>
          </div>
        </form>
      )}

      {!isLoading && groups.length === 0 && !showCreate ? (
        <div className="rounded-lg border border-gray-200 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-800 py-16 text-center">
          <FolderTree size={40} className="mx-auto mb-3 text-gray-300 dark:text-riot-navy-600" />
          <p className="text-sm text-gray-500 dark:text-riot-navy-300">No device groups yet</p>
          <p className="mt-1 text-xs text-gray-400 dark:text-riot-navy-400">Create a group to organize your devices.</p>
          <button onClick={() => setShowCreate(true)} className="mt-4 flex items-center gap-1.5 mx-auto rounded-md bg-riot-gold-400 px-4 py-2 text-sm text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600">
            <Plus size={14} /> Add group
          </button>
        </div>
      ) : (
        <DataTable
          title="Device groups"
          totalCount={data?.pagination.count ?? groups.length}
          columns={columns}
          data={groups}
          keyExtractor={(g) => g.groupId}
          onRowClick={(g) => navigate(`/groups/${g.groupId}`)}
          selectable
          actions={
            <button onClick={() => setShowCreate(true)} className="flex items-center gap-1.5 rounded-md bg-riot-gold-400 px-3.5 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600">
              <Plus size={14} /> Add group
            </button>
          }
        />
      )}
    </div>
  );
}
